import { ReactNode, useMemo } from 'react';

import {
  Divider,
  InfoTooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { dashesOrNum, getTendencyChange } from '@cast/utils';

import { TendencyChip } from 'components/chips/TendencyChip';
import { TendencyIndicator } from 'components/indicators';
import { ReliabilityMetricsData } from 'features/cluster/_components/reliability/_utils/buildReliabilityChartData';
import { MetricsCard } from 'features/cluster/cost-report/_components/metrics-card/MetricsCard';

import { ReliabilityMetricKey } from '../_constants/reliabilityMetrics';
import {
  formatLatency,
  formatLatencyWithUnit,
  isLatencyMetricKey,
} from '../_utils/formatLatency';

type ReliabilitySummarySectionProps = {
  data?: ReliabilityMetricsData;
  previousPeriodData?: ReliabilityMetricsData;
  showTendency?: boolean;
  variant?: 'cards' | 'inline';
};

type SummaryMetricConfig = {
  key: ReliabilityMetricKey;
  title: string;
  titleInfo: string;
  testId: string;
  unit: string;
};

const SUMMARY_METRICS: SummaryMetricConfig[] = [
  {
    key: 'requestRate',
    title: 'Request throughput',
    titleInfo: 'Average request throughput in the selected period.',
    testId: 'reliability-summary-request-throughput',
    unit: 'req/s',
  },
  {
    key: 'errorRate',
    title: 'Error rate',
    titleInfo: 'Average error rate in the selected period.',
    testId: 'reliability-summary-error-throughput',
    unit: 'req/s',
  },
  {
    key: 'latencyP95',
    title: 'Latency p95',
    titleInfo: 'Average p95 latency in the selected period.',
    testId: 'reliability-summary-latency-p95',
    unit: 'ms',
  },
];

const getMetricDisplay = (
  key: ReliabilityMetricKey,
  value: number | undefined,
  unit: string
): { value: ReactNode; unit: string } => {
  if (isLatencyMetricKey(key)) {
    const formatted = formatLatency(value, 2);
    return {
      value: formatted?.value ?? '--.--',
      unit: formatted?.unit ?? 'ms',
    };
  }

  return {
    value: dashesOrNum({ value, roundBy: 2 }),
    unit,
  };
};

const getTendencyDisplay = (
  key: ReliabilityMetricKey,
  diff: number | undefined,
  unit: string
): string => {
  if (isLatencyMetricKey(key)) {
    return formatLatencyWithUnit(diff, 2);
  }

  return `${dashesOrNum({ value: diff, roundBy: 2 })} ${unit}`;
};

const computeMetricValues = ({
  key,
  unit,
  data,
  previousPeriodData,
}: {
  key: ReliabilityMetricKey;
  unit: string;
  data: ReliabilityMetricsData | undefined;
  previousPeriodData: ReliabilityMetricsData | undefined;
}) => {
  const current = data?.[key]?.summary;
  const previous = previousPeriodData?.[key]?.summary;
  return {
    metricDisplay: getMetricDisplay(key, current, unit),
    tendency:
      current !== undefined && previous !== undefined
        ? getTendencyChange(current, previous)
        : undefined,
  };
};

export const ReliabilitySummarySection = ({
  data,
  previousPeriodData,
  showTendency = true,
  variant = 'cards',
}: ReliabilitySummarySectionProps) => {
  const computedMetrics = useMemo(
    () =>
      SUMMARY_METRICS.map((metric) => ({
        ...metric,
        ...computeMetricValues({
          key: metric.key,
          unit: metric.unit,
          data,
          previousPeriodData,
        }),
      })),
    [data, previousPeriodData]
  );

  if (variant === 'inline') {
    return (
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        gap={24}
        data-testid="reliability-summary-cards"
        sx={{
          px: 16,
          py: 8,
          backgroundColor: tokens.theme.bg.structural.surface,
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutral,
          borderRadius: 1,
        }}
      >
        {computedMetrics.map(
          ({
            key,
            title,
            titleInfo,
            testId,
            unit,
            metricDisplay,
            tendency,
          }) => (
            <Stack key={key} gap={4} flex={1} data-testid={testId}>
              <Stack direction="row" alignItems="center" gap={4}>
                <Typography
                  variant="L10R"
                  color={tokens.theme.fg.structural.secondary}
                  textTransform="uppercase"
                  noWrap
                >
                  {title}
                </Typography>
                <InfoTooltip title={titleInfo} placement="top" />
              </Stack>
              <Stack direction="row" alignItems="baseline" gap={8}>
                <Stack direction="row" alignItems="baseline" gap={4}>
                  <Typography
                    variant="h6"
                    noWrap
                    color={tokens.theme.fg.structural.primary}
                  >
                    {metricDisplay.value}
                  </Typography>
                  <Typography
                    variant="L10R"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    {metricDisplay.unit}
                  </Typography>
                </Stack>
                {showTendency && tendency && (
                  <TendencyChip
                    size="2xs"
                    tendency={tendency.tendency}
                    value={getTendencyDisplay(key, tendency.diff, unit)}
                  />
                )}
              </Stack>
            </Stack>
          )
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={16}
      data-testid="reliability-summary-cards"
    >
      {computedMetrics.map(
        ({ key, title, titleInfo, testId, unit, metricDisplay, tendency }) => (
          <MetricsCard
            key={key}
            title={title}
            titleInfo={titleInfo}
            testId={testId}
            sx={{ flex: 1 }}
            metrics={
              <Stack gap={8}>
                <Stack direction="row" alignItems="baseline" gap={4}>
                  <Typography
                    variant="h3"
                    noWrap
                    color={tokens.theme.fg.structural.primary}
                  >
                    {metricDisplay.value}
                  </Typography>
                  <Typography
                    variant="L10R"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    {metricDisplay.unit}
                  </Typography>
                </Stack>
                {showTendency && (
                  <TendencyIndicator
                    variant="arrow"
                    tendency={tendency?.tendency ?? 'stable'}
                    value={getTendencyDisplay(key, tendency?.diff, unit)}
                    sx={{ height: 16 }}
                    size={10}
                    valueProps={{ variant: 'L10M' }}
                  />
                )}
              </Stack>
            }
          />
        )
      )}
    </Stack>
  );
};
